import { Fragment, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useQuery } from '@tanstack/react-query'
import { useApiFetch } from '../lib/api'
import { useResolveAgentId } from '../lib/agentSlug'
import { useSectionAccess } from '../lib/roleAccess'
import { HomeIcon } from './icons/HomeIcon'
import { ChevronRightIcon } from './icons/ChevronRightIcon'
import type { AgentDetailResponse } from '../lib/types'

type T = TFunction<'translation', undefined>

/* URL segment (`:tab`) → `tabs.*` label, mirroring the alias table in
 * AgentDetail.tsx (R-NAV-8). Falls back to the raw segment for an unknown tab. */
function agentTabLabel(t: T, seg: string): string {
  switch (seg) {
    case 'agent-dna': return t('tabs.agentDna')
    case 'skills': return t('tabs.skills')
    case 'connections': return t('tabs.connections')
    case 'knowledge-base': return t('tabs.knowledgeBase')
    case 'guardrails': return t('tabs.guardrails')
    case 'agent-access': return t('tabs.access')
    default: return seg
  }
}

/* Mirrors the alias table in GlobalSettings.tsx (R-NAV-8). */
function globalSettingsTabLabel(t: T, seg: string): string {
  switch (seg) {
    case 'general': return t('tabs.general')
    case 'models': return t('tabs.models')
    case 'skills': return t('tabs.skills')
    case 'connections': return t('tabs.connections')
    case 'knowledge-base': return t('tabs.knowledgeBase')
    case 'guardrails': return t('tabs.guardrails')
    case 'studio-access': return t('tabs.studioAccess')
    default: return seg
  }
}

/* MambuMcpDetail.tsx's TabId doubles as the URL segment. */
function mcpTabLabel(t: T, seg: string): string {
  switch (seg) {
    case 'tenant': return t('mambuMcpDetail.tabs.tenant')
    case 'templates': return t('mambuMcpDetail.tabs.templates')
    case 'users': return t('mambuMcpDetail.tabs.users')
    default: return seg
  }
}

/* McpAnalyticsDetail.tsx's TabId doubles as the URL segment; the i18n keys
 * predate the rename and were not renamed with it. */
function mcpAnalyticsTabLabel(t: T, seg: string): string {
  switch (seg) {
    case 'tool-invocations': return t('mcpAnalytics.detail.tabs.invocations')
    case 'search-activity': return t('mcpAnalytics.detail.tabs.search')
    case 'invoke-activity': return t('mcpAnalytics.detail.tabs.invoke')
    default: return seg
  }
}

/* Bare overview sections with no sub-crumb: Home › **Section**. */
function simpleSectionLabel(t: T, section: string): string | null {
  switch (section) {
    case 'audit-log': return t('appShell.nav.auditLog')
    case 'marketplace': return t('appShell.nav.marketplace')
    case 'workflows': return t('appShell.nav.workflows')
    case 'agents-analytics': return t('appShell.nav.analytics')
    default: return null
  }
}

interface CrumbItem {
  label: string
  to?: string
  home?: boolean
}

/* Builds the section-specific crumb trail (excluding the leading Home crumb).
 * Returns null for an unrecognized bare section (caller renders nothing). */
function buildSectionCrumbs(
  t: T,
  section: string,
  seg1: string | undefined,
  seg2: string | undefined,
  agentDisplayName: string | undefined,
): CrumbItem[] | null {
  switch (section) {
    case 'agents': {
      const crumbs: CrumbItem[] = [{ label: t('appShell.nav.agents'), to: '/agents' }]
      if (seg1 && seg2) {
        crumbs.push({ label: agentDisplayName ?? seg1 })
        crumbs.push({ label: agentTabLabel(t, seg2) })
      }
      return crumbs
    }
    case 'mcp': {
      const crumbs: CrumbItem[] = [{ label: t('appShell.nav.mambuMcp'), to: '/mcp' }]
      if (seg1 && seg2) {
        crumbs.push({ label: t('mambuMcp.core.name') })
        crumbs.push({ label: mcpTabLabel(t, seg2) })
      }
      return crumbs
    }
    case 'global-settings': {
      const crumbs: CrumbItem[] = [
        { label: t('appShell.nav.globalSettings'), to: '/global-settings' },
      ]
      if (seg1) {
        crumbs.push({ label: globalSettingsTabLabel(t, seg1) })
      }
      return crumbs
    }
    case 'mcp-analytics': {
      const crumbs: CrumbItem[] = [
        { label: t('appShell.nav.mcpAnalytics'), to: '/mcp-analytics' },
      ]
      if (seg1 && seg2) {
        crumbs.push({ label: t('mambuMcp.core.name') })
        crumbs.push({ label: mcpAnalyticsTabLabel(t, seg2) })
      }
      return crumbs
    }
    default: {
      const simpleLabel = simpleSectionLabel(t, section)
      return simpleLabel === null ? null : [{ label: simpleLabel }]
    }
  }
}

function CrumbLink({ crumb, isLast, inner }: Readonly<{ crumb: CrumbItem; isLast: boolean; inner: ReactNode }>) {
  if (isLast) return <span className="crumb-current">{inner}</span>
  if (crumb.to) return <Link to={crumb.to}>{inner}</Link>
  return <span className="crumb-static">{inner}</span>
}

/**
 * Shell-level, route-derived breadcrumb bar (MS-740 AC2). Rendered once by
 * AppShell above `<Outlet/>`, mirroring the prototype's single
 * `#app-breadcrumb` (agent-studio.html:3613, `renderBreadcrumb` at :7167).
 *
 * Crumbs come from the current pathname, not local page state, so a tab
 * switch — which is now a real navigation — re-renders the trailing crumb
 * for free. The prototype's own renderer had to be called manually on every
 * tab click and its trailing crumb went stale when that call was missed;
 * deriving from the route avoids that class of bug entirely.
 */
export function Breadcrumbs() {
  const { t } = useTranslation()
  const location = useLocation()
  const apiFetch = useApiFetch()
  const { canOpen } = useSectionAccess()
  const segments = location.pathname.split('/').filter(Boolean)
  const [section, seg1, seg2] = segments

  /* Gated on `canOpen('agents')` for the reason stated in lib/roleAccess.ts:8-10.
     AppShell renders this bar on every route, so it sits OUTSIDE the
     `RequireSectionAccess` guard on `/agents/*`: a role without Agents access landing on an agent
     deep link (e.g. a stale `redirect_after_login`) mounted these two queries and
     403'd on both before the guard's redirect took effect. An empty slug disables
     the slug-resolution fetches too — see useResolveAgentId's `isEmpty`. */
  const idOrSlug = section === 'agents' && canOpen('agents') ? seg1 : undefined
  const { agentId: resolvedAgentId, isResolving } = useResolveAgentId(idOrSlug ?? '')
  const { data: agentData } = useQuery({
    queryKey: ['agent', resolvedAgentId],
    queryFn: () => apiFetch<AgentDetailResponse>(`/api/v1/agents/${resolvedAgentId}`),
    enabled: !!idOrSlug && !isResolving,
  })

  if (!section || section === 'home') return null

  const crumbs = buildSectionCrumbs(t, section, seg1, seg2, agentData?.agent.displayName)
  if (crumbs === null) return null

  const allCrumbs: CrumbItem[] = [{ label: t('appShell.nav.home'), to: '/home', home: true }, ...crumbs]

  return (
    <div className="auto-crumb">
      {allCrumbs.map((crumb, i) => {
        const isLast = i === allCrumbs.length - 1
        const inner = (
          <>
            {crumb.home && (
              <span className="crumb-home">
                <HomeIcon size={14} />
              </span>
            )}
            {crumb.label}
          </>
        )
        return (
          <Fragment key={`${crumb.label}-${i}`}>
            {i > 0 && (
              <span className="crumb-sep">
                <ChevronRightIcon size={13} />
              </span>
            )}
            <CrumbLink crumb={crumb} isLast={isLast} inner={inner} />
          </Fragment>
        )
      })}
    </div>
  )
}
